import type { WeavePopNotification } from '@frontend/types';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';

export type InboxMutedThreadCapability =
  | 'thread-status-indicators'
  | 'tags'
  | 'tasks'
  | 'sms-deletion'
  | 'unread-indicator';

export type InboxMutedFilterCapability =
  | 'quick-filters'
  | 'bulk-select'
  | 'actionable-tab'
  | 'filter-tags'
  | 'filter-tasks'
  | 'filter-ai-receptionist';

export type InboxMutedActionCapability =
  | 'mark-read-list-item'
  | 'mark-no-reply-needed'
  | 'archive-list-item'
  | 'archive-thread-header'
  | 'mark-unread-thread-header'
  | 'ai-receptionist-thread-toggle';

export type InboxMutedNavigationCapability = 'settings-messaging' | 'settings-tags' | 'settings-org-workspaces';

export type InboxMutedCapability =
  | InboxMutedThreadCapability
  | InboxMutedFilterCapability
  | InboxMutedActionCapability
  | InboxMutedNavigationCapability;

export const MUTED_INBOX_THREAD_CAPABILITIES = [
  'thread-status-indicators',
  'tags',
  'tasks',
  'sms-deletion',
  'unread-indicator',
] as const satisfies readonly InboxMutedThreadCapability[];

export const MUTED_INBOX_FILTER_CAPABILITIES = [
  'quick-filters',
  'bulk-select',
  'actionable-tab',
  'filter-tags',
  'filter-tasks',
  'filter-ai-receptionist',
] as const satisfies readonly InboxMutedFilterCapability[];

export const MUTED_INBOX_ACTION_CAPABILITIES = [
  'mark-read-list-item',
  'mark-no-reply-needed',
  'archive-list-item',
  'archive-thread-header',
  'mark-unread-thread-header',
  'ai-receptionist-thread-toggle',
] as const satisfies readonly InboxMutedActionCapability[];

// 'settings-org-workspaces' is in MUTED_INBOX_DISABLED_CAPABILITIES (shown disabled + tooltip)
export const MUTED_INBOX_NAVIGATION_CAPABILITIES = [
  'settings-messaging',
  'settings-tags',
] as const satisfies readonly InboxMutedNavigationCapability[];

/** Capabilities that are completely hidden (null render) when inbox is muted. */
export const MUTED_INBOX_CAPABILITIES: ReadonlySet<InboxMutedCapability> = new Set<InboxMutedCapability>([
  ...MUTED_INBOX_THREAD_CAPABILITIES,
  ...MUTED_INBOX_FILTER_CAPABILITIES,
  ...MUTED_INBOX_ACTION_CAPABILITIES,
  ...MUTED_INBOX_NAVIGATION_CAPABILITIES,
]);

/** Capabilities that are shown but disabled (grayed + tooltip) when inbox is muted. */
export const MUTED_INBOX_DISABLED_CAPABILITIES: ReadonlySet<InboxMutedCapability> = new Set<InboxMutedCapability>([
  'settings-org-workspaces',
]);

export const MUTED_POPUP_NOTIFICATION_TYPES = new Set<WeavePopNotification['type']>([
  'sms-message-new',
  'sms-tag',
  'ai-receptionist-state',
]);

/**
 * Notification Center (Firestore persisted) types suppressed when the inbox is muted.
 *
 * Same deny-by-default pattern as MUTED_POPUP_NOTIFICATION_TYPES, but uses
 * Firestore `NotificationType` enum values for the persisted center list.
 * When `isMuted` is true, these types are hidden from the Notification Center tray.
 */
export const MUTED_CENTER_NOTIFICATION_TYPES = new Set<NotificationType>([
  // Core SMS / inbox thread types
  NotificationType.SMS,
  NotificationType.SMS_TAG_V2,
  NotificationType.INBOX_THREAD_ITEM,
  NotificationType.INBOX_THREAD_ITEM_TAG,
  // Messaging-inbox supporting types
  NotificationType.SMS_TASK,
  NotificationType.SMS_MANUAL_SCHEDULED,
  NotificationType.SMS_PAUSE_SCHEDULED_THREAD,
  // AI Receptionist
  NotificationType.AI_RECEPTIONIST_STATE,
]);
